// Cinematic particle field - the atmospheric layer that makes every
// scene "breathe". No videos, no external assets, pure canvas so it
// runs at 60fps on phones and never introduces jank.

import type { ParticleMotif } from "./beats";

type Particle = {
  x: number;
  y: number;
  z: number;      // parallax depth 0..1
  r: number;      // radius
  vx: number;
  vy: number;
  a: number;      // alpha 0..1
  hue: number;
};

export type ParticleConfig = {
  count: number;
  drift: [number, number]; // ambient wind vx, vy per frame
  color: (p: Particle) => string;
  init: (p: Particle, w: number, h: number) => void;
  step: (p: Particle, w: number, h: number) => void;
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function defaultInit(p: Particle, w: number, h: number) {
  p.x = Math.random() * w;
  p.y = Math.random() * h;
  p.z = Math.random();
  p.r = rand(0.4, 2.6) * (0.3 + p.z);
  p.vx = rand(-0.15, 0.15);
  p.vy = rand(-0.05, 0.05);
  p.a = rand(0.18, 0.55) * p.z;
  p.hue = 0;
}

export function motifConfig(motif: ParticleMotif): ParticleConfig {
  switch (motif) {
    case "embers":
      return {
        count: 90,
        drift: [0.02, -0.12],
        color: (p) => `hsla(${20 + p.hue}, 92%, ${55 + p.z * 15}%, ${p.a})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.y = h + Math.random() * 40;
          p.vy = -rand(0.15, 0.55) * (0.4 + p.z);
          p.vx = rand(-0.25, 0.25);
          p.hue = rand(-8, 14);
          p.r = rand(0.6, 2.2);
          p.a = rand(0.35, 0.9);
        },
        step: (p, w, h) => {
          p.x += p.vx + Math.sin(p.y * 0.02) * 0.15;
          p.y += p.vy;
          p.a *= 0.995;
          if (p.y < -20 || p.a < 0.05) {
            p.y = h + 10;
            p.x = Math.random() * w;
            p.a = rand(0.35, 0.9);
          }
        },
      };
    case "bubbles":
      return {
        count: 55,
        drift: [0, -0.2],
        color: (p) => `hsla(190, 80%, ${75 + p.z * 15}%, ${p.a * 0.55})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.y = h + Math.random() * 60;
          p.vy = -rand(0.15, 0.55);
          p.r = rand(1.2, 4.5);
          p.a = rand(0.25, 0.7);
        },
        step: (p, w, h) => {
          p.x += Math.sin((p.y + p.z * 100) * 0.02) * 0.4;
          p.y += p.vy;
          if (p.y < -20) {
            p.y = h + 10;
            p.x = Math.random() * w;
          }
        },
      };
    case "stars":
      return {
        count: 220,
        drift: [0, 0],
        color: (p) => `hsla(220, 30%, 95%, ${p.a})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.r = rand(0.3, 1.3) * (0.4 + p.z);
          p.a = rand(0.3, 0.9);
        },
        step: (p) => {
          p.a += Math.sin(performance.now() * 0.001 + p.x) * 0.005;
          if (p.a < 0.15) p.a = 0.15;
          if (p.a > 0.95) p.a = 0.95;
        },
      };
    case "cells":
      return {
        count: 40,
        drift: [0.05, 0.03],
        color: (p) => `hsla(${340 + p.hue}, 70%, ${60 + p.z * 20}%, ${p.a * 0.4})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.r = rand(6, 22) * (0.5 + p.z);
          p.hue = rand(-15, 15);
          p.a = rand(0.15, 0.4);
        },
        step: (p, w, h) => {
          p.x += p.vx + Math.sin(performance.now() * 0.0005 + p.y * 0.01) * 0.3;
          p.y += p.vy + Math.cos(performance.now() * 0.0005 + p.x * 0.01) * 0.3;
          if (p.x < -40) p.x = w + 40;
          if (p.x > w + 40) p.x = -40;
          if (p.y < -40) p.y = h + 40;
          if (p.y > h + 40) p.y = -40;
        },
      };
    case "snow":
      return {
        count: 120,
        drift: [0.02, 0.35],
        color: (p) => `hsla(210, 30%, 98%, ${p.a})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.vy = rand(0.2, 0.7);
          p.r = rand(0.6, 2.4);
          p.a = rand(0.4, 0.85);
        },
        step: (p, w, h) => {
          p.x += Math.sin(p.y * 0.015) * 0.4;
          p.y += p.vy;
          if (p.y > h + 10) {
            p.y = -10;
            p.x = Math.random() * w;
          }
        },
      };
    case "spores":
      return {
        count: 70,
        drift: [0.06, 0.05],
        color: (p) => `hsla(${80 + p.hue}, 55%, ${70 + p.z * 15}%, ${p.a * 0.5})`,
        init: (p, w, h) => {
          defaultInit(p, w, h);
          p.hue = rand(-10, 30);
          p.r = rand(1.2, 3.4);
          p.a = rand(0.25, 0.6);
        },
        step: (p, w, h) => {
          p.x += Math.sin(performance.now() * 0.0007 + p.y * 0.02) * 0.5;
          p.y += Math.cos(performance.now() * 0.0007 + p.x * 0.02) * 0.3 + 0.08;
          if (p.y > h + 20) { p.y = -20; p.x = Math.random() * w; }
        },
      };
    case "dust":
    default:
      return {
        count: 80,
        drift: [0.05, 0.02],
        color: (p) => `hsla(35, 25%, 92%, ${p.a * 0.5})`,
        init: defaultInit,
        step: (p, w, h) => {
          p.x += p.vx + Math.sin(performance.now() * 0.0004 + p.y * 0.01) * 0.2;
          p.y += p.vy;
          if (p.x < -20) p.x = w + 20;
          if (p.x > w + 20) p.x = -20;
          if (p.y < -20) p.y = h + 20;
          if (p.y > h + 20) p.y = -20;
        },
      };
  }
}

export function runParticleField(canvas: HTMLCanvasElement, motif: ParticleMotif) {
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return () => {};

  const cfg = motifConfig(motif);
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  let particles: Particle[] = [];
  let raf = 0;
  let running = true;

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = Math.max(1, Math.floor(rect.width));
    height = Math.max(1, Math.floor(rect.height));
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (particles.length !== cfg.count) {
      particles = Array.from({ length: cfg.count }, () => {
        const p: Particle = { x: 0, y: 0, z: 0, r: 1, vx: 0, vy: 0, a: 1, hue: 0 };
        cfg.init(p, width, height);
        return p;
      });
    } else {
      particles.forEach((p) => { if (p.x > width || p.y > height) cfg.init(p, width, height); });
    }
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);

  const frame = () => {
    if (!running) return;
    ctx.clearRect(0, 0, width, height);
    for (const p of particles) {
      cfg.step(p, width, height);
      ctx.beginPath();
      ctx.fillStyle = cfg.color(p);
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    ro.disconnect();
  };
}
